import { useQuery } from "@tanstack/react-query";
import type { EmailOptInBannerModel } from "src/http/types";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { readEmailOptInSubscribed } from "./email-opt-in-subscription-storage";

export interface EmailOptInStatus {
  shouldShowEmailOptInBanner: boolean;
  email: string | null;
  isLoading: boolean;
}

/**
 * Determines whether the email opt-in banner should be shown for the current user.
 *
 * For logged-in users: calls the lazy-load endpoint `GetEmailOptInStatus` which runs the
 * server-side subscription check independently of the progressive carousel loading experiment
 * (101864). This fixes the bug where `EmailOptInBannerModel` was `null` for users in that
 * experiment variant.
 *
 * For anonymous users: falls back to localStorage — if `email_opt_in_subscribed` is `"1"`,
 * the user has already subscribed (e.g. via the EmailOptInPopup) and the banner is hidden.
 * This reuses the same key as `useEmailOptInPopupVisibility` so both components share state.
 *
 * @param isUserLoggedIn - Whether the current user is authenticated.
 */
export const useEmailOptInStatus = (isUserLoggedIn: boolean): EmailOptInStatus => {
  const createQueryFn = useCreateQueryFn();
  const { data, isLoading } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.EMAIL_OPT_IN_STATUS),
    queryFn: createQueryFn<EmailOptInBannerModel>({
      method: "GetEmailOptInStatus",
      params: [],
    }),
    enabled: isUserLoggedIn,
    staleTime: Infinity,
  });

  if (isUserLoggedIn) {
    return {
      shouldShowEmailOptInBanner: data?.shouldShowEmailOptInBanner ?? false,
      email: data?.email ?? null,
      isLoading,
    };
  }

  // Anonymous user: derive from localStorage subscription flag.
  const isSubscribed = readEmailOptInSubscribed();
  return {
    shouldShowEmailOptInBanner: !isSubscribed,
    email: null,
    isLoading: false,
  };
};
